import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { Modal } from "@/components/common/Modal";
import { jobsService } from "@/api/services/jobs.service";
import { jobToUpdateDto } from "@/utils/jobUtils";
import { toDateTime, getTodayAUS, formatDateTimeAUS } from "@/utils/dateTime";
import styles from "./ComplaintModal.module.css";
export const ComplaintModal = ({ isOpen, onClose, jobId, onSaved, }) => {
    const [job, setJob] = useState(null);
    const [complaintNotes, setComplaintNotes] = useState("");
    const [complaintDate, setComplaintDate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const loadJob = useCallback(async () => {
        if (!jobId)
            return;
        setLoading(true);
        setError(null);
        try {
            const res = await jobsService.getJob(jobId);
            const jobData = res.data;
            setJob(jobData);
            const notes = jobData.complaintnotes ?? jobData.complaintNotes ?? "";
            const dateRaw = jobData.complaintdate ?? jobData.complaintDate ?? null;
            setComplaintNotes(notes);
            setComplaintDate(dateRaw);
        }
        catch (err) {
            console.error("Failed to load job for complaint:", err);
            setError("Failed to load complaint. Please try again.");
        }
        finally {
            setLoading(false);
        }
    }, [jobId]);
    useEffect(() => {
        if (isOpen && jobId) {
            loadJob();
        }
    }, [isOpen, jobId, loadJob]);
    const handleSave = async (e) => {
        e.preventDefault();
        if (!jobId || saving || !job)
            return;
        setSaving(true);
        setError(null);
        try {
            const complaintDateISO = toDateTime(getTodayAUS(), "00:00");
            const payload = jobToUpdateDto(job, {
                complaintNotes: complaintNotes.trim() || undefined,
                complaintDate: complaintDateISO,
            });
            await jobsService.updateJob(jobId, payload);
            onSaved?.();
            onClose();
        }
        catch (err) {
            console.error("Failed to save complaint:", err);
            setError("Failed to save complaint. Please try again.");
        }
        finally {
            setSaving(false);
        }
    };
    const handleClose = () => {
        setError(null);
        onClose();
    };
    return (_jsx(Modal, { isOpen: isOpen, onClose: handleClose, children: _jsxs("div", { className: styles.container, children: [_jsx("h2", { className: styles.title, children: "Complaint" }), _jsx("p", { className: styles.subtitle, children: "We are really sorry to hear that something did not meet your expectation on this job. Kindly provide details of your complaint below." }), loading ? (_jsx("div", { className: styles.loading, children: "Loading..." })) : (_jsxs("form", { onSubmit: handleSave, children: [complaintDate && (_jsxs("div", { className: styles.lastUpdated, children: ["Last updated: ", formatDateTimeAUS(complaintDate)] })), _jsxs("div", { className: styles.field, children: [_jsx("label", { className: styles.label, htmlFor: "complaint-notes", children: "Complaint note" }), _jsx("textarea", { id: "complaint-notes", className: styles.textarea, value: complaintNotes, onChange: (e) => setComplaintNotes(e.target.value), placeholder: "Describe your complaint...", rows: 5, disabled: saving })] }), error && _jsx("div", { className: styles.error, children: error }), _jsxs("div", { className: styles.actions, children: [_jsx("button", { type: "button", className: styles.cancelButton, onClick: handleClose, disabled: saving, children: "Cancel" }), _jsx("button", { type: "submit", className: styles.saveButton, disabled: saving, children: saving ? "Sending..." : "Send" })] })] }))] }) }));
};
